export const ROLE_LEVEL = {
  technicien: 1,
  manager: 2,
  admin: 3,
  super_admin: 4,
} as const;

export type AccessRole = keyof typeof ROLE_LEVEL;

export function getRoleLevel(role: string | null | undefined): number {
  return ROLE_LEVEL[(role || "technicien") as AccessRole] ?? 1;
}

export const ACCESS_ROLE_LABELS: Record<AccessRole, string> = {
  technicien: "Technicien",
  manager: "Manager",
  admin: "Admin",
  super_admin: "Super Admin",
};

// ─── Niveau minimum par module (basé sur access_role) ────────────────────────
// Source de vérité unique — utilisée par les gardes de routes ET la sidebar.
// Ne jamais utiliser `profile.role` (champ métier/service) pour décider d'un accès.
export const MODULE_MIN_LEVEL = {
  // technicien (1) — tous les utilisateurs authentifiés
  "/": 1,
  "/procedures": 1,
  "/missions": 1,
  "/tickets": 1,
  "/chats": 1,
  "/tasks": 1,
  "/rappels": 1,
  "/contacts": 1,
  "/clients": 1,
  "/resellers": 1,
  "/robots": 1,
  "/sav": 1,
  "/products": 1,
  "/prospects": 1,
  "/devis": 1,

  // manager (2) — coordination + commercial avancé
  "/commandes": 2,
  "/sous-traitance": 2,
  "/achats": 2,
  "/stock": 2,
  "/vehicles": 2,

  // admin (3) — coffre-fort
  "/coffre": 3,

  // super_admin (4) — paramètres et outils d'administration
  "/parametres/partenaires": 4,
  "/parametres/emails": 4,
  "/parametres/rappels": 4,
  "/partenaires": 4,
  "/admin": 4,
  "/contacts/admin": 4,
} as const;

/**
 * Retourne le niveau minimum requis pour accéder à un chemin de route.
 * Cherche d'abord un match exact, puis le préfixe le plus long.
 */
export function getRouteMinLevel(path: string): number {
  const keys = Object.keys(MODULE_MIN_LEVEL) as Array<keyof typeof MODULE_MIN_LEVEL>;
  // Match exact
  if (MODULE_MIN_LEVEL[path as keyof typeof MODULE_MIN_LEVEL] !== undefined) {
    return MODULE_MIN_LEVEL[path as keyof typeof MODULE_MIN_LEVEL];
  }
  // Match par préfixe le plus long
  const match = keys
    .filter(k => path.startsWith(k + "/") || path.startsWith(k + "?"))
    .sort((a, b) => b.length - a.length)[0];
  return match ? MODULE_MIN_LEVEL[match] : 1;
}

/**
 * Vérifie si un utilisateur peut accéder à une route, uniquement basé sur access_role.
 */
export function canAccessRoute(accessRole: string | null | undefined, path: string): boolean {
  return getRoleLevel(accessRole) >= getRouteMinLevel(path);
}

// ─── Helper d'action centralisé ──────────────────────────────────────────────
//
// canDo(accessRole) → true si le compte a un niveau d'accès ≥ admin (3).
// super_admin (4) = toujours vrai.  manager (2) / technicien (1) = faux.
// À utiliser à la place de `profile?.role === 'admin'` dans tous les canEdit,
// canCreate, canDelete, etc.  Les droits liés au champ métier (planification,
// commercial…) s'ajoutent EN PLUS : `canDo(accessRole) || role === 'planification'`.
//
export function canDo(accessRole: string | null | undefined): boolean {
  return getRoleLevel(accessRole) >= ROLE_LEVEL.admin;
}

// isSuperAdmin(accessRole) → true uniquement pour le niveau super_admin (4).
// À utiliser pour les actions exclusivement réservées au super_admin :
//   • modification du champ accessRole d'un collaborateur (escalade de privilèges)
//   • accès au module Paramètres et à ses onglets système
export function isSuperAdmin(accessRole: string | null | undefined): boolean {
  return getRoleLevel(accessRole) >= ROLE_LEVEL.super_admin;
}

// ─── Helpers spécifiques ─────────────────────────────────────────────────────

export function canAccessVault(role: string | null | undefined): boolean {
  return getRoleLevel(role) >= ROLE_LEVEL.admin;
}

export function canAccessParametres(role: string | null | undefined): boolean {
  return getRoleLevel(role) >= ROLE_LEVEL.super_admin;
}

export function canDeleteTask(role: string | null | undefined): boolean {
  return getRoleLevel(role) >= ROLE_LEVEL.admin;
}
